/**
 * The project-tab preferences on the server (vfs-service `/api/v1/me/settings`, section `tabs`), so they follow the
 * person to any browser. utils/projectTabsModel.js keeps them in this browser's localStorage: the fast cache read first
 * and the fallback when the sandbox cannot be reached.
 *
 * - Once per page (loadTabPrefs, from useTabPrefs): the server's `tabs` are read. Already saved there (`saved: true`):
 *   they replace this browser's. Never saved there: this browser's values go up, once (the migration from
 *   localStorage), with `saved: true`. A change this browser could not send before (`flow.projectTabs.pending`, while
 *   offline) goes up instead of being replaced. No vfs-service, or an older one without `tabs`: localStorage only.
 * - Each change (onTabPrefsStored): marked pending, then sent (one PUT per burst of changes); sent, not pending.
 */
import { getSettings, updateSettings } from '../services/SettingsService';
import { cacheServerTabs, onTabPrefsStored, readLocalTabs } from './projectTabsModel';

const PENDING_KEY = 'flow.projectTabs.pending';
export const TABS_SAVE_DELAY_MS = 400;

// 'idle' (not read yet) | 'loading' | 'server' (the server keeps them) | 'local' (this browser only) | 'offline'
let state = 'idle';
let loading = null;
let timer = null;
let api = { get: getSettings, put: updateSettings };

const pending = () => { try { return localStorage.getItem(PENDING_KEY) === '1'; } catch { return false; } };
const setPending = (on) => {
  try { if (on) { localStorage.setItem(PENDING_KEY, '1'); } else { localStorage.removeItem(PENDING_KEY); } } catch { /* sent again next time */ }
};

/** What goes up: everything this browser keeps, marked as saved on the server. */
export const tabsPayload = () => ({ tabs: { ...readLocalTabs(), saved: true } });

async function send() {
  try {
    const answer = await api.put(tabsPayload());
    if (answer?.tabs) { setPending(false); }
    return true;
  } catch {
    return false; // still pending: sent on the next change or page load
  }
}

/** Reads the server's preferences once per page (see above); resolves to the state. */
export function loadTabPrefs() {
  if (loading) { return loading; }
  state = 'loading';
  loading = Promise.resolve()
    .then(() => api.get())
    .then(async (settings) => {
      if (!settings?.tabs) { state = 'local'; return state; }
      state = 'server';
      if (pending() || !settings.tabs.saved) {
        await send();
      } else {
        cacheServerTabs(settings.tabs);
      }
      return state;
    })
    .catch(() => { state = 'offline'; return state; });
  return loading;
}

/** A change written to this browser: pending, then sent once the server is known to keep them. */
export function queueTabPrefsSave() {
  if (state === 'local') { return; }
  setPending(true);
  if (state !== 'server') { return; } // loadTabPrefs sends it once it knows
  clearTimeout(timer);
  timer = setTimeout(() => { timer = null; send(); }, TABS_SAVE_DELAY_MS);
}

onTabPrefsStored(queueTabPrefsSave);

/** For tests: a fresh page, with these settings calls. */
export function resetTabPrefsSync(next = { get: getSettings, put: updateSettings }) {
  clearTimeout(timer);
  timer = null;
  state = 'idle';
  loading = null;
  api = next;
}

export const tabPrefsSyncState = () => state;
